/**
 * WEB COPY of DocumentsDashboardScreen.tsx — picked by the web bundler ahead
 * of the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Web app Phase 5: the documents the server keeps (added on the phone or
 * here) plus the physical-copy records in the vault, in one list. Drop files
 * anywhere on the page, or choose several at once; each is encrypted in the
 * browser and stored as it arrives. Filter by category, sort, move several
 * to another category, and see how much of the plan's space is used.
 *
 * Opening or adding a file needs the vault key, so the recovery code is
 * asked for then (decision of 2026-09-18); listing does not need it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useUserSelection } from '../../../contexts/UserSelectionContext';

import backIcon from '../../../assets/icons/back.png';
import { vaultService } from '../../../services/vaultService';
import {
  CloudDocument,
  cloudDocuments,
  displayNameFor,
  documentTypeFor,
  MAX_FILE_BYTES,
} from '../../../services/documents/cloudDocuments.web';
import { createLogger } from '../../../utils/logger';
import { ms, s, vs } from '../../../utils/responsive';
import {
  browser,
  CATEGORIES,
  categoryId,
  categoryName,
  chooseFiles,
  errorMessage,
  FileDragEvent,
  formatBytes,
  formatDate,
  withVaultKey,
} from './documentsShared.web';

const logger = createLogger('DocumentsDashboard.web');

type Row =
  | { kind: 'file'; id: string; name: string; category: string; type: string; size: number; date: string; doc: CloudDocument }
  | { kind: 'physical'; id: string; name: string; category: string; type: string; size: 0; date: ''; location: string };

type SortKey = 'newest' | 'name' | 'size';

type Upload = { key: string; name: string; status: 'waiting' | 'working' | 'done' | 'failed'; message?: string };

const RETURN_TO = { root: 'Documents', routes: [{ name: 'DocumentsDashboardScreen' }] };

export const DocumentsDashboardScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  // K5 / Step 5.6: a successor viewing the owner's documents after a
  // verified death. Read-only; the files open with the key the owner left.
  const targetUserId: string | undefined = route.params?.targetUserId;
  const viewOnly = !!targetUserId && !!route.params?.viewOnly;
  const { availablePrimaryUsers } = useUserSelection();
  const ownerName = viewOnly ? availablePrimaryUsers.find((u) => u.id === targetUserId)?.name || 'this person' : null;
  const [files, setFiles] = useState<CloudDocument[]>([]);
  const [physical, setPhysical] = useState<any[]>([]);
  const [usage, setUsage] = useState({ used: 0, limit: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [sort, setSort] = useState<SortKey>('newest');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [dragging, setDragging] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const dragDepth = useRef(0);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [list, metadata] = await Promise.all([
        cloudDocuments.list(targetUserId),
        viewOnly ? Promise.resolve([]) : vaultService.getAllDocuments().catch(() => []),
      ]);
      setFiles(list.files);
      setUsage({ used: list.usedBytes, limit: list.limitBytes });
      setPhysical(Array.isArray(metadata) ? metadata : []);
    } catch (error) {
      logger.error('Could not load documents:', error);
      setLoadError(errorMessage(error, 'Your documents could not be loaded. Try again.'));
    } finally {
      setLoading(false);
    }
  }, [targetUserId, viewOnly]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  useEffect(() => {
    const c = route.params?.category;
    if (c) setFilter(categoryId(c));
  }, [route.params?.category]);

  const rows: Row[] = useMemo(() => {
    const all: Row[] = [
      ...files.map((d) => ({
        kind: 'file' as const,
        id: d.documentId,
        name: d.fileName,
        category: categoryId(d.category),
        type: d.documentType || 'Other',
        size: d.fileSizeBytes ?? 0,
        date: d.createdAt,
        doc: d,
      })),
      ...physical.map((d) => ({
        kind: 'physical' as const,
        id: d.id,
        name: d.documentName,
        category: categoryId(d.category),
        type: d.documentType || '',
        size: 0 as const,
        date: '' as const,
        location: d.location || '',
      })),
    ];
    const shown = filter === 'all' ? all : all.filter((r) => r.category === filter);
    return [...shown].sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'size') return b.size - a.size;
      return (b.date || '').localeCompare(a.date || '');
    });
  }, [files, physical, filter, sort]);

  const counts = useMemo(() => {
    const out: Record<string, number> = { all: files.length + physical.length };
    for (const r of [...files.map((f) => f.category), ...physical.map((p) => p.category)]) {
      const id = categoryId(r);
      out[id] = (out[id] ?? 0) + 1;
    }
    return out;
  }, [files, physical]);

  const canStore = usage.limit > 0 && !viewOnly;

  // ---- Upload ----------------------------------------------------------------

  const uploadFiles = useCallback(
    async (chosen: File[]) => {
      if (chosen.length === 0) return;
      if (!canStore) {
        Alert.alert(
          'Document storage comes with Plus and Premium',
          'Plus keeps 500MB of documents and Premium 2GB, on your phone and here.',
          [
            { text: 'Not now', style: 'cancel' },
            { text: 'See plans', onPress: () => navigation.navigate('Profile', { screen: 'SubscriptionManagement' }) },
          ],
        );
        return;
      }
      const queue: Upload[] = chosen.map((f, i) => ({ key: `${Date.now()}-${i}`, name: f.name, status: 'waiting' }));
      setUploads((prev) => [...prev.filter((u) => u.status !== 'done'), ...queue]);
      const setStatus = (key: string, patch: Partial<Upload>) =>
        setUploads((prev) => prev.map((u) => (u.key === key ? { ...u, ...patch } : u)));

      const ran = await withVaultKey(RETURN_TO, async () => {
        for (let i = 0; i < chosen.length; i += 1) {
          const file = chosen[i];
          const { key } = queue[i];
          if (file.size > MAX_FILE_BYTES) {
            setStatus(key, { status: 'failed', message: 'Too large (the limit is about 50MB)' });
            continue;
          }
          setStatus(key, { status: 'working' });
          try {
            await cloudDocuments.upload(file, {
              fileName: displayNameFor(file.name),
              category: filter === 'all' ? 'other' : filter,
              documentType: documentTypeFor(file.name, file.type),
              tags: ['Uploaded'],
            });
            setStatus(key, { status: 'done' });
          } catch (error) {
            // A locked vault stops the whole queue (withVaultKey prompts).
            if ((error as Error)?.name === 'VaultLockedError') {
              setStatus(key, { status: 'failed', message: 'Recovery code needed' });
              throw error;
            }
            logger.error('Upload failed:', error);
            setStatus(key, { status: 'failed', message: errorMessage(error, 'Could not store this file') });
          }
        }
      }).catch((error) => {
        logger.error('Upload queue stopped:', error);
        return false;
      });
      if (!ran) {
        const keys = new Set(queue.map((q) => q.key));
        setUploads((prev) =>
          prev.map((u) =>
            keys.has(u.key) && u.status === 'waiting' ? { ...u, status: 'failed', message: 'Recovery code needed' } : u,
          ),
        );
      }
      await load();
    },
    [canStore, filter, load, navigation],
  );

  const onChooseFiles = async () => uploadFiles(await chooseFiles(true));

  // Drop files anywhere on the page.
  useEffect(() => {
    const hasFiles = (e: FileDragEvent) => Array.from(e.dataTransfer?.types ?? ([] as string[])).includes('Files');
    const onEnter = (e: FileDragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    };
    const onOver = (e: FileDragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const onLeave = (e: FileDragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    const onDrop = (e: FileDragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      uploadFiles(Array.from(e.dataTransfer?.files ?? ([] as File[])));
    };
    browser.window.addEventListener('dragenter', onEnter);
    browser.window.addEventListener('dragover', onOver);
    browser.window.addEventListener('dragleave', onLeave);
    browser.window.addEventListener('drop', onDrop);
    return () => {
      browser.window.removeEventListener('dragenter', onEnter);
      browser.window.removeEventListener('dragover', onOver);
      browser.window.removeEventListener('dragleave', onLeave);
      browser.window.removeEventListener('drop', onDrop);
    };
  }, [uploadFiles]);

  // ---- Selection and bulk actions ----------------------------------------------

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedRows = rows.filter((r) => selected.has(r.id));

  const moveSelected = async (category: string) => {
    setBulkBusy(true);
    try {
      for (const r of selectedRows) {
        if (r.kind === 'file') {
          await cloudDocuments.update(r.id, { category });
        } else {
          const d = physical.find((p) => p.id === r.id);
          await vaultService.storeDocumentMetadata(r.id, {
            category,
            documentType: d.documentType,
            documentName: d.documentName,
            location: d.location,
            purpose: d.purpose,
          });
        }
      }
      setSelected(new Set());
      await load();
    } catch (error) {
      Alert.alert('Could not move the documents', errorMessage(error, 'Try again.'));
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteSelected = () => {
    const n = selectedRows.length;
    Alert.alert(
      n === 1 ? 'Delete this document?' : `Delete ${n} documents?`,
      'They are removed here and from your phone. This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setBulkBusy(true);
            try {
              for (const r of selectedRows) {
                if (r.kind === 'file') await cloudDocuments.remove(r.id);
                else await vaultService.deleteDocumentMetadata(r.id);
              }
              setSelected(new Set());
              await load();
            } catch (error) {
              Alert.alert('Could not delete every document', errorMessage(error, 'Try again.'));
            } finally {
              setBulkBusy(false);
            }
          },
        },
      ],
    );
  };

  // ---- Render ------------------------------------------------------------------

  const percent = usage.limit > 0 ? Math.min(100, (usage.used / usage.limit) * 100) : 0;

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Dashboard'))}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Documents</Text>
        <View style={styles.backButton} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.column}>
          {/* Storage and add */}
          <View style={styles.card}>
            <View style={styles.rowBetween}>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>{viewOnly ? `${ownerName}'s documents` : 'Your documents'}</Text>
                <Text style={styles.muted}>
                  {viewOnly
                    ? 'Decrypted in your browser with the key they left for you. You can open and download them; nothing here can be changed.'
                    : 'Encrypted in your browser or on your phone before they are stored. Documents you add here show in the FinalKey app, and the other way round.'}
                </Text>
              </View>
            </View>

            {canStore ? (
              <View style={styles.quota}>
                <View style={styles.quotaTrack}>
                  <View
                    style={[
                      styles.quotaFill,
                      { width: `${percent}%` as any },
                      percent >= 90 && styles.quotaFillFull,
                    ]}
                  />
                </View>
                <Text style={styles.quotaText}>
                  {formatBytes(usage.used) || '0 KB'} of {formatBytes(usage.limit)} used
                </Text>
              </View>
            ) : (
              !loading && (
                <View style={styles.notice}>
                  <Text style={styles.noticeText}>
                    Storing document files comes with Plus (500MB) and Premium (2GB). You can still
                    record where your paper documents are kept.
                  </Text>
                  <TouchableOpacity onPress={() => navigation.navigate('Profile', { screen: 'SubscriptionManagement' })}>
                    <Text style={styles.link}>See plans</Text>
                  </TouchableOpacity>
                </View>
              )
            )}

            <View style={styles.actions}>
              {canStore && (
                <TouchableOpacity style={styles.primaryButton} onPress={onChooseFiles} accessibilityRole="button">
                  <Text style={styles.primaryButtonText}>Upload files</Text>
                </TouchableOpacity>
              )}
              {!viewOnly && (
                <TouchableOpacity
                  style={styles.secondaryButton}
                  onPress={() =>
                    navigation.navigate('DocumentsInputScreen', {
                      category: filter === 'all' ? undefined : categoryName(filter),
                    })
                  }
                  accessibilityRole="button"
                >
                  <Text style={styles.secondaryButtonText}>Add a document with details</Text>
                </TouchableOpacity>
              )}
            </View>
            {canStore && <Text style={styles.hint}>Or drop files anywhere on this page. Up to about 50MB each.</Text>}

            {uploads.length > 0 && (
              <View style={styles.uploads}>
                {uploads.map((u) => (
                  <View key={u.key} style={styles.uploadRow}>
                    <Text style={styles.uploadName} numberOfLines={1}>
                      {u.name}
                    </Text>
                    {u.status === 'working' && <ActivityIndicator size="small" color="#29BFC0" />}
                    <Text
                      style={[
                        styles.uploadStatus,
                        u.status === 'failed' && styles.uploadFailed,
                        u.status === 'done' && styles.uploadDone,
                      ]}
                    >
                      {u.status === 'waiting'
                        ? 'Waiting'
                        : u.status === 'working'
                          ? 'Encrypting and storing'
                          : u.status === 'done'
                            ? 'Stored'
                            : u.message || 'Failed'}
                    </Text>
                  </View>
                ))}
                {uploads.every((u) => u.status === 'done' || u.status === 'failed') && (
                  <TouchableOpacity onPress={() => setUploads([])}>
                    <Text style={styles.link}>Clear</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>

          {/* Filters */}
          <View style={styles.chips}>
            {[{ id: 'all', name: 'All' }, ...CATEGORIES].map((c) => (
              <TouchableOpacity
                key={c.id}
                style={[styles.chip, filter === c.id && styles.chipActive]}
                onPress={() => {
                  setFilter(c.id);
                  setSelected(new Set());
                }}
                accessibilityRole="button"
                accessibilityState={{ selected: filter === c.id }}
              >
                <Text style={[styles.chipText, filter === c.id && styles.chipTextActive]}>
                  {c.name}
                  {counts[c.id] ? ` · ${counts[c.id]}` : ''}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.rowBetween}>
            <View style={styles.sortRow}>
              <Text style={styles.muted}>Sort:</Text>
              {(['newest', 'name', 'size'] as SortKey[]).map((k) => (
                <TouchableOpacity key={k} onPress={() => setSort(k)} accessibilityRole="button">
                  <Text style={[styles.sortOption, sort === k && styles.sortOptionActive]}>
                    {k === 'newest' ? 'Newest' : k === 'name' ? 'Name' : 'Size'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {rows.length > 0 && !viewOnly && (
              <TouchableOpacity
                onPress={() =>
                  setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)))
                }
              >
                <Text style={styles.link}>{selected.size === rows.length ? 'Select none' : 'Select all'}</Text>
              </TouchableOpacity>
            )}
          </View>

          {selected.size > 0 && (
            <View style={styles.bulkBar}>
              <Text style={styles.bulkText}>{selected.size} selected. Move to:</Text>
              <View style={styles.bulkChips}>
                {CATEGORIES.map((c) => (
                  <TouchableOpacity
                    key={c.id}
                    style={styles.bulkChip}
                    disabled={bulkBusy}
                    onPress={() => moveSelected(c.id)}
                  >
                    <Text style={styles.bulkChipText}>{c.name}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <TouchableOpacity style={styles.bulkDelete} disabled={bulkBusy} onPress={deleteSelected}>
                <Text style={styles.bulkDeleteText}>{bulkBusy ? 'Working…' : 'Delete'}</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* List */}
          <View style={styles.listCard}>
            {loading ? (
              <View style={styles.empty}>
                <ActivityIndicator color="#29BFC0" />
              </View>
            ) : loadError ? (
              <View style={styles.empty}>
                <Text style={styles.muted}>{loadError}</Text>
                <TouchableOpacity onPress={() => { setLoading(true); load(); }}>
                  <Text style={styles.link}>Try again</Text>
                </TouchableOpacity>
              </View>
            ) : rows.length === 0 ? (
              <View style={styles.empty}>
                <Text style={styles.emptyTitle}>
                  {filter === 'all' ? 'No documents yet' : `No ${categoryName(filter).toLowerCase()} documents yet`}
                </Text>
                <Text style={styles.muted}>
                  {canStore
                    ? 'Upload a will, a policy or a deed, or drop the files here.'
                    : 'Add where a paper document is kept so the people you choose can find it.'}
                </Text>
              </View>
            ) : (
              rows.map((r, i) => (
                <View key={r.id} style={[styles.docRow, i > 0 && styles.docRowBorder]}>
                  {!viewOnly && (
                    <TouchableOpacity
                      onPress={() => toggle(r.id)}
                      style={[styles.checkbox, selected.has(r.id) && styles.checkboxOn]}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: selected.has(r.id) }}
                      accessibilityLabel={`Select ${r.name}`}
                    >
                      {selected.has(r.id) && <Text style={styles.checkmark}>✓</Text>}
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={styles.docMain}
                    onPress={() =>
                      navigation.navigate('DocumentsDataScreen', { documentId: r.id, kind: r.kind, targetUserId, viewOnly })
                    }
                    accessibilityRole="button"
                  >
                    <View style={styles.docBadge}>
                      <Text style={styles.docBadgeText}>{r.kind === 'file' ? (r.type || 'File').slice(0, 5) : 'Paper'}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.docName} numberOfLines={1}>
                        {r.name}
                      </Text>
                      <Text style={styles.docMeta} numberOfLines={1}>
                        {r.kind === 'file'
                          ? [categoryName(r.category), formatBytes(r.size), formatDate(r.date)].filter(Boolean).join(' · ')
                          : [categoryName(r.category), r.location ? `Kept at ${r.location}` : 'Paper copy'].join(' · ')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                </View>
              ))
            )}
          </View>
        </View>
      </ScrollView>

      {dragging && canStore && (
        <View style={styles.dropOverlay} pointerEvents="none">
          <View style={styles.dropBox}>
            <Text style={styles.dropText}>
              Drop to encrypt and store{filter === 'all' ? '' : ` in ${categoryName(filter)}`}
            </Text>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F3F6F8' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  backButton: { padding: s(4), width: ms(32) },
  backIcon: { width: ms(24), height: ms(24), resizeMode: 'contain' },
  headerTitle: { fontSize: ms(16), fontWeight: '600', color: '#1A1A1A' },
  content: { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 120 },
  column: { width: '100%', maxWidth: 760, alignSelf: 'center' },
  card: { backgroundColor: '#FFFFFF', borderRadius: 16, padding: 20, marginBottom: 16 },
  cardTitle: { fontSize: 18, fontWeight: '700', color: '#1C2C4F', marginBottom: 6 },
  muted: { fontSize: 14, lineHeight: 20, color: '#4B5563' },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  quota: { marginTop: 16 },
  quotaTrack: { height: 8, borderRadius: 4, backgroundColor: '#E5EAF0', overflow: 'hidden' },
  quotaFill: { height: 8, borderRadius: 4, backgroundColor: '#29BFC0' },
  quotaFillFull: { backgroundColor: '#E0763A' },
  quotaText: { marginTop: 6, fontSize: 13, color: '#4B5563' },
  notice: { marginTop: 16, padding: 14, borderRadius: 12, backgroundColor: '#FFF6EC' },
  noticeText: { fontSize: 14, lineHeight: 20, color: '#7A4A12', marginBottom: 6 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 18 },
  primaryButton: { backgroundColor: '#29BFC0', borderRadius: 24, paddingVertical: 12, paddingHorizontal: 22 },
  primaryButtonText: { color: '#FFFFFF', fontWeight: '700', fontSize: 15 },
  secondaryButton: { borderRadius: 24, paddingVertical: 12, paddingHorizontal: 22, borderWidth: 1.5, borderColor: '#1C2C4F' },
  secondaryButtonText: { color: '#1C2C4F', fontWeight: '600', fontSize: 15 },
  hint: { marginTop: 10, fontSize: 13, color: '#6B7280' },
  uploads: { marginTop: 14, borderTopWidth: 1, borderTopColor: '#EEF1F4', paddingTop: 10, gap: 6 },
  uploadRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  uploadName: { flex: 1, fontSize: 14, color: '#1A1A1A' },
  uploadStatus: { fontSize: 13, color: '#6B7280' },
  uploadFailed: { color: '#C2410C' },
  uploadDone: { color: '#0F8B8D' },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  chip: { borderRadius: 18, paddingVertical: 7, paddingHorizontal: 14, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#DDE3EA' },
  chipActive: { backgroundColor: '#1C2C4F', borderColor: '#1C2C4F' },
  chipText: { fontSize: 14, color: '#1C2C4F' },
  chipTextActive: { color: '#FFFFFF', fontWeight: '600' },
  sortRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  sortOption: { fontSize: 14, color: '#4B5563' },
  sortOptionActive: { color: '#1C2C4F', fontWeight: '700', textDecorationLine: 'underline' },
  link: { fontSize: 14, color: '#0F8B8D', fontWeight: '600' },
  bulkBar: { marginTop: 12, padding: 14, borderRadius: 12, backgroundColor: '#1C2C4F', gap: 10 },
  bulkText: { color: '#FFFFFF', fontSize: 14, fontWeight: '600' },
  bulkChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  bulkChip: { borderRadius: 14, paddingVertical: 5, paddingHorizontal: 12, backgroundColor: 'rgba(255,255,255,0.15)' },
  bulkChipText: { color: '#FFFFFF', fontSize: 13 },
  bulkDelete: { alignSelf: 'flex-start', borderRadius: 14, paddingVertical: 6, paddingHorizontal: 14, backgroundColor: '#E0763A' },
  bulkDeleteText: { color: '#FFFFFF', fontSize: 13, fontWeight: '700' },
  listCard: { marginTop: 12, backgroundColor: '#FFFFFF', borderRadius: 16, paddingHorizontal: 16 },
  empty: { paddingVertical: 36, alignItems: 'center', gap: 8 },
  emptyTitle: { fontSize: 16, fontWeight: '700', color: '#1C2C4F' },
  docRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, gap: 12 },
  docRowBorder: { borderTopWidth: 1, borderTopColor: '#EEF1F4' },
  checkbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 1.5, borderColor: '#9AA6B5', alignItems: 'center', justifyContent: 'center' },
  checkboxOn: { backgroundColor: '#29BFC0', borderColor: '#29BFC0' },
  checkmark: { color: '#FFFFFF', fontSize: 14, fontWeight: '700', lineHeight: 16 },
  docMain: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 },
  docBadge: { width: 48, height: 48, borderRadius: 10, backgroundColor: '#EAF7F7', alignItems: 'center', justifyContent: 'center' },
  docBadgeText: { fontSize: 11, fontWeight: '700', color: '#0F8B8D' },
  docName: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  docMeta: { marginTop: 2, fontSize: 13, color: '#6B7280' },
  dropOverlay: {
    position: 'fixed' as unknown as 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(28,44,79,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  dropBox: { borderRadius: 20, borderWidth: 3, borderStyle: 'dashed', borderColor: '#FFFFFF', paddingVertical: 48, paddingHorizontal: 56 },
  dropText: { color: '#FFFFFF', fontSize: 20, fontWeight: '700' },
});

export default DocumentsDashboardScreen;
